import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function Customer360() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [customerType, setCustomerType] = useState(searchParams.get('type') || 'all');

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    
    const customerId = searchParams.get('id');
    const type = searchParams.get('type');
    if (customerId && type) {
      loadCustomerData(type, customerId);
    }
  }, []);

  const handleSearch = async () => {
    if (searchQuery.length < 2) return;
    
    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ query: searchQuery });
      if (customerType !== 'all') {
        params.append('customer_type', customerType);
      }
      
      const response = await axios.get(`${API}/api/customer-360/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const loadCustomerData = async (type, id) => {
    setLoading(true);
    setSelectedCustomer({ type, id });
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/customer-360/${type}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerData(response.data);
      setSearchParams({ type, id });
    } catch (error) {
      console.error('Failed to load customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    const type = customer.customer_type;
    loadCustomerData(type, customer.id);
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const customerTypes = [
    { id: 'all', label: 'All', icon: '👥' },
    { id: 'franchisee', label: 'Franchisee', icon: '🏢' },
    { id: 'b2c', label: 'B2C', icon: '🛍️' },
    { id: 'b2r', label: 'B2R', icon: '🏪' },
    { id: 'b2b', label: 'B2B', icon: '🏭' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'payments', label: 'Payments' },
    { id: 'documents', label: 'Documents' },
    { id: 'communications', label: 'Communications' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('active') || s.includes('live') || s.includes('paid') || s.includes('completed')) 
      return 'bg-green-100 text-green-800';
    if (s.includes('pending') || s.includes('progress') || s.includes('setup')) 
      return 'bg-yellow-100 text-yellow-800';
    if (s.includes('inactive') || s.includes('cancelled') || s.includes('rejected')) 
      return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'franchisee': return '🏢';
      case 'lead': return '🎯';
      case 'b2c': return '🛍️';
      case 'b2r': return '🏪';
      case 'b2b': return '🏭';
      default: return '👤';
    }
  };

  return (
    <Layout user={user} onLogout={handleLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#2b3380' }}>Customer 360°</h1>
          <p className="text-gray-600">Complete customer journey, LTV, and interaction history</p>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          {/* Customer Type Tabs */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {customerTypes.map(type => (
              <button
                key={type.id}
                onClick={() => setCustomerType(type.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
                  customerType === type.id ? 'text-white' : 'bg-gray-100 hover:bg-gray-200'
                }`}
                style={customerType === type.id ? { backgroundColor: '#2b3380' } : {}}
              >
                <span>{type.icon}</span>
                <span>{type.label}</span>
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="flex gap-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name, phone, email, or reference number..."
              className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              disabled={searching || searchQuery.length < 2}
              className="px-6 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#2b3380' }}
            >
              {searching ? 'Searching...' : '🔍 Search'}
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 border rounded-lg divide-y max-h-80 overflow-y-auto">
              {searchResults.map((result, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSelectCustomer(result)}
                  className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl">{getTypeIcon(result.customer_type)}</span>
                    <div>
                      <div className="font-medium" style={{ color: '#2b3380' }}>
                        {result.name || result.company_name || 'Unknown'}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-3">
                        {result.phone && <span>📞 {result.phone}</span>}
                        {result.reference && <span className="text-blue-600">{result.reference}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusBadge(result.status)}`}>
                      {result.status}
                    </span>
                    <span className="text-xs text-gray-500 uppercase bg-gray-100 px-2 py-1 rounded">
                      {result.customer_type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Customer Details */}
        {loading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-gray-500">Loading customer data...</div>
          </div>
        ) : customerData ? (
          <div className="space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                {/* Profile Info */}
                <div className="flex-1">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-lg flex items-center justify-center text-3xl" 
                         style={{ backgroundColor: '#e8eaf6' }}>
                      {getTypeIcon(customerData.profile?.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="text-xl font-bold" style={{ color: '#2b3380' }}>
                          {customerData.profile?.name || customerData.profile?.company_name}
                        </h2>
                        <span className={`px-3 py-1 text-xs rounded-full font-medium ${getStatusBadge(customerData.profile?.status)}`}>
                          {customerData.profile?.status}
                        </span>
                      </div>
                      {customerData.profile?.company_name && customerData.profile?.name && (
                        <p className="text-gray-600">{customerData.profile?.company_name}</p>
                      )}
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                        {customerData.profile?.phone && (
                          <span>📞 {customerData.profile.phone}</span>
                        )}
                        {customerData.profile?.email && (
                          <span>✉️ {customerData.profile.email}</span>
                        )}
                        {customerData.profile?.city && (
                          <span>📍 {customerData.profile.city}</span>
                        )}
                        {customerData.profile?.created_at && (
                          <span>📅 Since {formatDate(customerData.profile.created_at)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-700">{formatCurrency(customerData.stats?.ltv)}</div>
                    <div className="text-xs text-green-600">Lifetime Value</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-700">{customerData.stats?.total_orders || 0}</div>
                    <div className="text-xs text-blue-600">Total Orders</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-700">{formatCurrency(customerData.stats?.outstanding_balance)}</div>
                    <div className="text-xs text-orange-600">Outstanding</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-700">{customerData.stats?.engagement_score || 0}%</div>
                    <div className="text-xs text-purple-600">Engagement</div>
                  </div>
                </div>
              </div>

              {/* Journey Stages */}
              {customerData.journey_stage && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Journey Progress</h3>
                  <div className="flex items-center justify-between overflow-x-auto pb-2">
                    {customerData.journey_stage.stages?.map((stage, idx) => (
                      <React.Fragment key={stage.id}>
                        <div className={`flex flex-col items-center min-w-[70px] ${stage.current ? 'scale-110' : ''}`}>
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all ${
                            stage.completed ? 'bg-green-500 text-white' :
                            stage.current ? 'text-white ring-4 ring-blue-200' :
                            'bg-gray-200 text-gray-500'
                          }`}
                          style={stage.current ? { backgroundColor: '#2b3380' } : {}}>
                            {stage.completed ? '✓' : stage.icon}
                          </div>
                          <span className={`text-xs mt-2 font-medium text-center ${
                            stage.current ? 'text-blue-800' : stage.completed ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            {stage.name}
                          </span>
                        </div>
                        {idx < customerData.journey_stage.stages.length - 1 && (
                          <div className={`flex-1 h-1 mx-2 rounded min-w-[20px] ${
                            stage.completed ? 'bg-green-500' : 'bg-gray-200'
                          }`} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow">
              <div className="border-b">
                <div className="flex space-x-4 p-4">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeTab === tab.id ? 'text-white' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      style={activeTab === tab.id ? { backgroundColor: '#2b3380' } : {}}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Timeline */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4" style={{ color: '#2b3380' }}>Recent Activity</h3>
                      <div className="space-y-4">
                        {customerData.timeline?.slice(0, 5).map((event, idx) => (
                          <div key={idx} className="flex gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              event.color === 'green' ? 'bg-green-100' :
                              event.color === 'orange' ? 'bg-orange-100' :
                              event.color === 'purple' ? 'bg-purple-100' :
                              'bg-blue-100'
                            }`}>
                              <span className="text-lg">{event.icon}</span>
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{event.title}</div>
                              <div className="text-sm text-gray-600">{event.description}</div>
                              <div className="text-xs text-gray-400 mt-1">{formatDate(event.timestamp)}</div>
                            </div>
                          </div>
                        ))}
                        {(!customerData.timeline || customerData.timeline.length === 0) && (
                          <p className="text-gray-500 text-center py-8">No activity recorded</p>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4" style={{ color: '#2b3380' }}>Financial Summary</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Total Revenue</span>
                          <span className="font-semibold">{formatCurrency(customerData.stats?.total_revenue)}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Total Payments</span>
                          <span className="font-semibold text-green-600">{formatCurrency(customerData.stats?.total_payments)}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Pending Payments</span>
                          <span className="font-semibold text-orange-600">{formatCurrency(customerData.stats?.pending_payments)}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Outstanding Balance</span>
                          <span className="font-semibold text-red-600">{formatCurrency(customerData.stats?.outstanding_balance)}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">Days as Customer</span>
                          <span className="font-semibold">{customerData.stats?.days_as_customer || 0} days</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Timeline Tab */}
                {activeTab === 'timeline' && (
                  <div className="max-h-[500px] overflow-y-auto">
                    {customerData.timeline && customerData.timeline.length > 0 ? (
                      <div className="relative pl-8">
                        <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                        <div className="space-y-6">
                          {customerData.timeline.map((event, idx) => (
                            <div key={idx} className="relative">
                              <div className={`absolute -left-5 w-6 h-6 rounded-full flex items-center justify-center text-sm ${
                                event.color === 'green' ? 'bg-green-500 text-white' :
                                event.color === 'orange' ? 'bg-orange-500 text-white' :
                                event.color === 'purple' ? 'bg-purple-500 text-white' :
                                'bg-blue-500 text-white'
                              }`}>
                                {event.icon}
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4 ml-4">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <div className="font-semibold text-gray-900">{event.title}</div>
                                    <div className="text-sm text-gray-600 mt-1">{event.description}</div>
                                  </div>
                                  <div className="text-xs text-gray-500">{formatDate(event.timestamp)}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-12">No timeline events</p>
                    )}
                  </div>
                )}

                {/* Payments Tab */}
                {activeTab === 'payments' && (
                  <div className="space-y-6">
                    {/* Payment Demands */}
                    {customerData.payment_demands && customerData.payment_demands.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4" style={{ color: '#2b3380' }}>Payment Demands</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Milestone</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Due Date</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {customerData.payment_demands.map((demand, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium">{demand.milestone || demand.description}</td>
                                  <td className="px-4 py-3">{formatCurrency(demand.amount)}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(demand.due_date)}</td>
                                  <td className="px-4 py-3">
                                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusBadge(demand.status)}`}>
                                      {demand.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Payment Collections */}
                    {customerData.payment_collections && customerData.payment_collections.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4" style={{ color: '#2b3380' }}>Payment Collections</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Reference</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Mode</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {customerData.payment_collections.map((collection, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium">{collection.reference_number || collection.id}</td>
                                  <td className="px-4 py-3 text-green-600 font-semibold">{formatCurrency(collection.amount)}</td>
                                  <td className="px-4 py-3 text-sm">{collection.payment_mode}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(collection.payment_date || collection.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Orders */}
                    {customerData.orders && customerData.orders.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4" style={{ color: '#2b3380' }}>Orders</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Order #</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {customerData.orders.map((order, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium">{order.order_number || order.id}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(order.created_at)}</td>
                                  <td className="px-4 py-3 font-semibold">{formatCurrency(order.total_amount)}</td>
                                  <td className="px-4 py-3">
                                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusBadge(order.status)}`}>
                                      {order.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {(!customerData.payment_demands?.length && !customerData.payment_collections?.length && !customerData.orders?.length) && (
                      <p className="text-gray-500 text-center py-12">No payment records found</p>
                    )}
                  </div>
                )}

                {/* Documents Tab */}
                {activeTab === 'documents' && (
                  <div>
                    {customerData.documents && customerData.documents.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {customerData.documents.map((doc, idx) => (
                          <div key={idx} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start gap-3">
                              <span className="text-3xl">📄</span>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{doc.document_name || doc.name}</div>
                                <div className="text-sm text-gray-500">{doc.document_type || doc.type}</div>
                                <div className="text-xs text-gray-400 mt-1">{formatDate(doc.created_at)}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-12">No documents uploaded</p>
                    )}
                  </div>
                )}

                {/* Communications Tab */}
                {activeTab === 'communications' && (
                  <div>
                    {customerData.communications && customerData.communications.length > 0 ? (
                      <div className="space-y-4">
                        {customerData.communications.map((comm, idx) => (
                          <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                              comm.type === 'whatsapp' ? 'bg-green-100' :
                              comm.type === 'email' ? 'bg-blue-100' :
                              'bg-purple-100'
                            }`}>
                              {comm.type === 'whatsapp' ? '💬' :
                               comm.type === 'email' ? '✉️' : '📞'}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{comm.subject}</div>
                              <div className="text-sm text-gray-500">{formatDate(comm.date)}</div>
                            </div>
                            {comm.status && (
                              <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusBadge(comm.status)}`}>
                                {comm.status}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-12">No communication history</p>
                    )}

                    {/* Support Tickets */}
                    {customerData.tickets && customerData.tickets.length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-lg font-semibold mb-4" style={{ color: '#2b3380' }}>Support Tickets</h3>
                        <div className="space-y-4">
                          {customerData.tickets.map((ticket, idx) => (
                            <div key={idx} className="border rounded-lg p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="font-medium text-gray-900">{ticket.subject}</div>
                                  <div className="text-sm text-gray-600 mt-1">{ticket.description}</div>
                                </div>
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusBadge(ticket.status)}`}>
                                  {ticket.status}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400 mt-2">{formatDate(ticket.created_at)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-xl font-semibold mb-2" style={{ color: '#2b3380' }}>Search for a Customer</h3>
            <p className="text-gray-600">Use the search bar above to find any customer by name, phone, email, or reference number</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Customer360;
